import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MarketingFooter, MarketingHeader } from './MarketingChrome';
import { HowItWorks } from './HowItWorks';
import { MintButton } from './MarketingButtons';
import { DodoMark } from './Logo';
import { strands } from './marketingContent';
import {
  CONTAINER,
  EYEBROW,
  FONT_BODY,
  FONT_DISPLAY,
  FONT_MONO,
  SECTION_H2,
  SECTION_PAD,
} from './marketingStyles';

// Public landing page -- redesign 2026-09-30 (Figma "DodoSkills" page,
// direction B "Four doors", owner-approved).
//
// What changed and why (design principles, in the order a visitor meets
// them):
// - Hierarchy: ONE message above the fold ("shows exactly what each child
//   is missing"), then four doors -- teacher, parent, school, student --
//   so each visitor finds their own way in within one screen instead of
//   reading nine sections to work out if it's for them.
// - Proximity: each door groups who it's for, what they get, and a single
//   action. No free-floating buttons.
// - Emphasis: one solid call to action per door; the dark band is used
//   once, at the end.
// - Proof before promise: section 2 shows the example class report (the
//   actual value -- the "why", not just a score) before any claims.
// - Less scrolling: nine sections down to six. Removed the six-card
//   "workspace areas" grid (feature inventory, low value to a first-time
//   visitor) and the separate "For schools" band (now the School door plus
//   the Schools tab in How it works).
// - Umbrella brand: DodoSkills is the parent brand; Maths is the first
//   subject. The subjects strip says so without promising dates.
//
// Unchanged: every route (sign-up with ?role=, /learn for students, /auth/
// sign-in), the section ids the header/footer link to (how-it-works,
// standards, curriculum, schools), and the privacy claims. The example
// report uses anonymous references only, and its numbers are internally
// consistent (24 students; 21 / 17 / 12 secure; 7 of the 12 who aren't
// secure on regrouping share one error pattern).

type Door = {
  id: string;
  who: string;
  line: string;
  cta: string;
  to: string;
};

const doors: Door[] = [
  {
    id: 'teacher',
    who: 'Teacher',
    line: 'Assign practice to your class and see each student’s skill gaps — with the reason they’re stuck.',
    cta: 'Start free as a teacher',
    to: '/auth/sign-up?role=teacher',
  },
  {
    id: 'parent',
    who: 'Parent',
    line: 'Set short practice at home and see how your child is going, skill by skill, in plain words.',
    cta: 'Start free as a parent',
    to: '/auth/sign-up?role=parent_carer',
  },
  {
    id: 'schools',
    who: 'School',
    line: 'For school admins: register your school, see your teachers, and follow results by year level.',
    cta: 'Register your school',
    to: '/auth/sign-up?role=school',
  },
];

const trustPoints = [
  'Years 1–8',
  'Australian Curriculum aligned',
  'No student names needed',
  'Free to use',
];

const reportRows = [
  { skill: 'Place value to 10,000', secure: 21, status: 'Secure' },
  {
    skill: 'Multiplication facts to 10 × 10',
    secure: 17,
    status: 'Still learning',
  },
  {
    skill: 'Subtraction with regrouping',
    secure: 12,
    status: 'Still learning',
  },
] as const;
const CLASS_SIZE = 24;

// Token colour pairs, each >= 4.7:1 (same as the app's own status chips).
// The labels are the product's own words (results pages say "Secure" and
// "Still learning"; owner approved 31 Oct).
const statusStyle: Record<
  (typeof reportRows)[number]['status'],
  { bg: string; text: string; bar: string }
> = {
  Secure: { bg: '#edf3ef', text: '#1d6b52', bar: '#1d6b52' },
  'Still learning': { bg: '#fdf6ec', text: '#b45309', bar: '#b45309' },
};

function Check() {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-mint">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1d6b52"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
  );
}

function ClassReport() {
  return (
    <figure
      aria-label="Example class report"
      className="flex w-full flex-col gap-4 rounded-xl border border-line bg-white p-5 shadow-card sm:p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className={`${FONT_DISPLAY} text-[20px] leading-tight font-bold text-ink`}
          >
            Year 4 · Number &amp; Algebra
          </p>
          <p className="mt-1 text-[13px] text-muted">
            This week · {CLASS_SIZE} students
          </p>
        </div>
        <span className="shrink-0 rounded-md border border-line bg-paper px-2 py-0.5 text-[11px] font-semibold tracking-wide text-muted uppercase">
          Example
        </span>
      </div>
      <ul className="flex flex-col gap-4">
        {reportRows.map((row) => {
          const s = statusStyle[row.status];
          return (
            <li key={row.skill} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 truncate text-[14px] font-semibold text-ink">
                  {row.skill}
                </p>
                <span
                  className="shrink-0 rounded-md px-2 py-0.5 text-[12px] font-semibold"
                  style={{ backgroundColor: s.bg, color: s.text }}
                >
                  {row.status}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-paper-2">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${String((row.secure / CLASS_SIZE) * 100)}%`,
                    backgroundColor: s.bar,
                  }}
                />
              </div>
              <p className="text-[12px] text-muted">
                {row.secure} of {CLASS_SIZE} students secure
              </p>
            </li>
          );
        })}
      </ul>
      <div className="rounded-lg border border-accent/25 bg-accent-soft p-4">
        <p className="text-[12px] font-bold tracking-wide text-accent uppercase">
          Error pattern · 7 students
        </p>
        <p className="mt-1.5 text-[14px] leading-[1.5] text-ink">
          Taking the smaller digit from the larger in each column instead of
          regrouping.
        </p>
      </div>
      <p className={`${FONT_MONO} text-[11px] text-muted`}>
        Students shown by anonymous reference — never by name.
      </p>
    </figure>
  );
}

export function LandingPage() {
  // Arriving from a footer page's section link ("/#standards" etc.):
  // scroll to that section once the page has rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <div
      className={`${FONT_BODY} dm-workspace flex w-full flex-col items-start bg-paper`}
    >
      <MarketingHeader />

      <main id="main-content" tabIndex={-1} className="w-full">
        {/* ============ 1. HERO + FOUR DOORS ============ */}
        <section className="w-full px-5 pt-10 pb-12 sm:px-6 lg:px-20 lg:pt-14 lg:pb-14">
          <div className={`${CONTAINER} flex flex-col gap-8 lg:gap-10`}>
            <div className="flex max-w-[900px] flex-col gap-4">
              <p className={EYEBROW}>DodoSkills Maths · Years 1–8</p>
              <h1
                className={`${FONT_DISPLAY} text-[38px] leading-[1.04] font-bold text-ink sm:text-[52px] lg:text-[64px]`}
              >
                Maths practice that shows exactly what each child is missing.
              </h1>
              <p className="max-w-[640px] text-[17px] leading-[1.55] text-ink-soft">
                Short, focused questions mapped to the Australian Curriculum.
                When a student gets stuck, you see why — not just a score. No
                games. No points. No student names.
              </p>
              <ul
                aria-label="At a glance"
                className="mt-1 flex flex-wrap gap-x-6 gap-y-2"
              >
                {trustPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-2 text-[14px] font-semibold text-forest-800"
                  >
                    <Check />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div
              role="list"
              aria-label="Choose who you are"
              className="grid w-full gap-3 sm:grid-cols-2 lg:gap-4 xl:grid-cols-4"
            >
              {doors.map((door) => (
                // Phone: a compact row (text + round arrow button) so the
                // four doors fit in about one screen. sm and up: a card
                // with a full-width labelled button. One link per door
                // either way -- the label is visually hidden on phones,
                // not duplicated.
                <div
                  key={door.id}
                  id={door.id}
                  role="listitem"
                  className="flex scroll-mt-24 items-center gap-4 rounded-xl border border-line bg-white p-4 sm:flex-col sm:items-stretch sm:p-6"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-1 sm:gap-2">
                    <p className="hidden text-[13px] font-semibold text-muted sm:block">
                      I’m a
                    </p>
                    <h2
                      className={`${FONT_DISPLAY} text-[24px] leading-none font-bold text-ink sm:text-[30px] lg:text-[34px]`}
                    >
                      <span className="sm:hidden">
                        I’m a {door.who.toLowerCase()}
                      </span>
                      <span className="hidden sm:inline">{door.who}</span>
                    </h2>
                    <p className="text-[14px] leading-[1.45] text-ink-soft sm:text-[15px] sm:leading-[1.5]">
                      {door.line}
                    </p>
                  </div>
                  <Link
                    to={door.to}
                    className={`${FONT_BODY} flex size-11 shrink-0 items-center justify-center rounded-full bg-forest-800 text-[15px] font-semibold whitespace-nowrap text-white transition-colors hover:bg-forest-950 sm:h-12 sm:w-auto sm:rounded-lg sm:px-6`}
                  >
                    <span className="sr-only sm:not-sr-only">{door.cta}</span>
                    <span aria-hidden="true" className="text-[18px] sm:hidden">
                      →
                    </span>
                  </Link>
                </div>
              ))}
              {/* On phones the student card comes first (design audit
                  2026-10-04): it was the fourth card, below the first
                  screen, and a phone is where a student is most likely to
                  arrive. Wider screens keep the original order. */}
              <div
                role="listitem"
                className="order-first flex flex-col gap-3 rounded-xl border-[1.5px] border-forest-600/30 bg-sage p-4 sm:order-none sm:gap-4 sm:p-6"
              >
                <div className="flex flex-1 flex-col gap-1 sm:gap-2">
                  <p className="hidden text-[13px] font-semibold text-forest-600 sm:block">
                    I’m a
                  </p>
                  <h2
                    className={`${FONT_DISPLAY} text-[24px] leading-none font-bold text-forest-950 sm:text-[30px] lg:text-[34px]`}
                  >
                    <span className="sm:hidden">I’m a student</span>
                    <span className="hidden sm:inline">Student</span>
                  </h2>
                  <p className="text-[14px] leading-[1.45] text-forest-800 sm:text-[15px] sm:leading-[1.5]">
                    Got a code from your teacher or parent? Type it in and
                    start. No account or password.
                  </p>
                </div>
                <MintButton to="/learn">
                  I'm a student — enter my code
                </MintButton>
              </div>
            </div>

            <p className="text-[14px] text-ink-soft">
              Already have an account?{' '}
              <Link
                to="/auth/sign-in"
                className="font-semibold text-forest-800 underline underline-offset-4"
              >
                Log in
              </Link>
            </p>
          </div>
        </section>

        {/* ============ 2. PROOF: THE CLASS REPORT ============ */}
        <section className={`${SECTION_PAD} bg-white`}>
          <div
            className={`${CONTAINER} grid items-center gap-10 xl:grid-cols-[1fr_520px] xl:gap-16`}
          >
            <div className="flex flex-col gap-5">
              <p className={EYEBROW}>What you get</p>
              <h2 className={SECTION_H2}>
                Not just a score. The reason behind it.
              </h2>
              <p className="max-w-[560px] text-[16px] leading-[1.6] text-ink-soft">
                Every answer is recorded against a curriculum skill. DodoSkills
                groups the mistakes, so you can see which skills are secure,
                where errors cluster, and who needs a follow-up — at a glance.
              </p>
              <ul className="flex flex-col gap-3">
                {[
                  'Skill-by-skill progress for the whole class or one child',
                  'The most common mistakes, and how many students made each one',
                  'Plain-language results for parents',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-[15px] text-ink"
                  >
                    <span className="mt-0.5">
                      <Check />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <ClassReport />
          </div>
        </section>

        {/* ============ 3. HOW IT WORKS (per audience) ============ */}
        <HowItWorks />

        {/* ============ 4. WHY NO GAMES + PRIVACY ============ */}
        <section
          id="standards"
          className={`${SECTION_PAD} scroll-mt-24 bg-white`}
        >
          <div className={`${CONTAINER} flex flex-col gap-8`}>
            <div className="flex max-w-[960px] flex-col gap-3">
              <p className={EYEBROW}>Why no games</p>
              <h2 className={SECTION_H2}>
                Calm practice builds understanding that lasts.
              </h2>
            </div>
            <div className="grid w-full gap-4 lg:grid-cols-3">
              {[
                {
                  title: 'Thinking, not guessing',
                  body: 'No stars, sounds or streaks. Students work each question through and can ask for a calm hint when they’re stuck.',
                },
                {
                  title: 'The student is never the record',
                  body: 'No names, birthdays or addresses. Learners appear as “Student 1”, “Student 2”, and practise with anonymous codes. Only you see any nickname you add.',
                },
                {
                  title: 'Mapped to the curriculum',
                  body: 'Every question maps to an Australian Curriculum skill, so progress means real mastery — not made-up levels.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex flex-col gap-2 rounded-xl border border-line bg-paper p-6"
                >
                  <h3
                    className={`${FONT_DISPLAY} text-[22px] leading-tight font-bold text-ink`}
                  >
                    {item.title}
                  </h3>
                  <p className="text-[15px] leading-[1.55] text-ink-soft">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 5. CURRICULUM + SUBJECTS ============ */}
        <section id="curriculum" className={`${SECTION_PAD} scroll-mt-24`}>
          <div className={`${CONTAINER} flex flex-col gap-8`}>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex max-w-[640px] flex-col gap-3">
                <p className={EYEBROW}>Curriculum</p>
                <h2 className={SECTION_H2}>
                  Every maths strand, Years 1 to 8.
                </h2>
              </div>
              <Link
                to="/guide"
                className="w-fit text-[15px] font-semibold text-forest-800 underline underline-offset-4"
              >
                Read the how-to guides
              </Link>
            </div>
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
              {strands.map((strand) => (
                <div
                  key={strand.title}
                  className="flex items-center gap-4 rounded-xl border border-line bg-white p-5"
                >
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-mint">
                    <img src={strand.icon} alt="" className="size-6" />
                  </span>
                  <div className="flex flex-col gap-1">
                    <h3
                      className={`${FONT_DISPLAY} text-[20px] leading-tight font-bold text-ink`}
                    >
                      {strand.title}
                    </h3>
                    <p className="text-[13px] font-semibold text-forest-600">
                      {strand.years[0]}–{strand.years[strand.years.length - 1]}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Umbrella brand: Maths now, more subjects later. No dates. */}
            <div className="flex flex-col gap-4 rounded-xl border border-line bg-white p-5 lg:flex-row lg:items-center lg:justify-between lg:p-6">
              <div className="flex items-center gap-4">
                <DodoMark size={44} subject="umbrella" className="shrink-0" />
                <div>
                  <p
                    className={`${FONT_DISPLAY} text-[20px] font-bold text-ink`}
                  >
                    One home for school learning
                  </p>
                  <p className="text-[14px] text-ink-soft">
                    DodoSkills starts with Maths. More subjects are coming —
                    same calm approach, same account.
                  </p>
                </div>
              </div>
              <ul className="flex shrink-0 flex-wrap gap-2">
                <li className="rounded-full bg-forest-950 px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap text-mint">
                  Maths · Available now
                </li>
                <li className="rounded-full border border-dashed border-line-strong bg-paper px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap text-ink-soft">
                  More subjects · Coming
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ============ 6. CLOSING BAND ============ */}
        <section className="flex w-full flex-col items-center gap-5 bg-forest-950 px-5 py-14 text-center sm:px-6 lg:px-20 lg:py-16">
          <h2
            className={`${FONT_DISPLAY} max-w-[760px] text-[28px] leading-[1.15] font-bold text-white lg:text-[38px]`}
          >
            A child who can explain their reasoning has learned more than one
            who collected 500 stars.
          </h2>
          <p className="text-[15px] text-[#a9d2bf]">
            Free to use. No student names. Set up in minutes.
          </p>
          <MintButton to="/auth/sign-up">Get started free</MintButton>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
